import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseStudyAPI } from '../../features/case-study/api/casestudyAPI';
import './CaseStudy.css';

/**
 * In-case reasoning + knowledge MCQs (graph-driven case_quiz_* → TG/LG on answer).
 * Uses the same decision/option visual language as the rest of the case player.
 */
const CaseQuizPanel = ({ caseStudyId, onContinueToWriting, onBackToDecision }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [phase, setPhase] = useState('reasoning');
  const [index, setIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  const questions = useMemo(() => {
    if (!payload) return [];
    return phase === 'reasoning' ? payload.reasoning || [] : payload.knowledge || [];
  }, [payload, phase]);

  const current = questions[index] || null;

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        setLoading(true);
        setError(null);
        const quizzes = await caseStudyAPI.getCaseQuizzes(caseStudyId);
        if (cancelled) return;
        setPayload(quizzes);
        if ((quizzes.reasoning || []).length === 0 && (quizzes.knowledge || []).length > 0) {
          setPhase('knowledge');
        }
        const attempt = await caseStudyAPI.startCaseQuizAttempt(caseStudyId, {
          assessment_type: 'quiz',
        });
        if (cancelled) return;
        setAttemptId(attempt.attempt_id);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.data?.error || err?.message || 'Failed to load case quizzes';
        if (err?.status === 401) {
          setError('Please sign in again to take the case quizzes.');
          navigate('/login', {
            state: { from: { pathname: `/case-study/${caseStudyId}` } },
            replace: true,
          });
          return;
        }
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [caseStudyId, navigate]);

  const goNext = async () => {
    setLastResult(null);
    setSelectedOptionId(null);
    if (index + 1 < questions.length) {
      setIndex(index + 1);
      return;
    }
    if (phase === 'reasoning' && (payload?.knowledge || []).length > 0) {
      setPhase('knowledge');
      setIndex(0);
      return;
    }
    try {
      if (attemptId) await caseStudyAPI.completeCaseQuizAttempt(attemptId);
    } catch (err) {
      console.warn('Complete quiz attempt:', err);
    }
    setPhase('done');
  };

  const handleSubmit = async () => {
    if (!selectedOptionId || !current || !attemptId || submitting) return;
    setSubmitting(true);
    try {
      const result = await caseStudyAPI.submitCaseQuizAnswer(attemptId, {
        question_id: current.id,
        selected_option_id: selectedOptionId,
      });
      setLastResult(result);
      setScore((s) => ({
        answered: s.answered + 1,
        correct: s.correct + (result.is_correct ? 1 : 0),
      }));
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="decision-section-animated natively-scrollable case-quiz-section">
        <div className="decision-wrapper case-quiz-wrapper">
          <div className="section-eyebrow">QUIZ</div>
          <div className="case-quiz-status">Loading reasoning &amp; knowledge questions…</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="decision-section-animated natively-scrollable case-quiz-section">
        <div className="decision-wrapper case-quiz-wrapper">
          <div className="section-eyebrow">QUIZ</div>
          <div className="decision-header-block">
            <h2 className="decision-main-title">Could not load quizzes</h2>
            <div className="section-divider" />
            <p className="decision-main-subtitle writing-task-subtitle">{error}</p>
          </div>
          <div className="feedback-actions case-quiz-actions">
            {typeof onBackToDecision === 'function' ? (
              <button type="button" className="try-again-btn" onClick={onBackToDecision}>
                Back to Act IV
              </button>
            ) : null}
            {typeof onContinueToWriting === 'function' ? (
              <button type="button" className="decision-submit-btn" onClick={onContinueToWriting}>
                Continue to Oxford Tutorial
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  if (!payload?.counts?.total) {
    return (
      <section className="decision-section-animated natively-scrollable case-quiz-section">
        <div className="decision-wrapper case-quiz-wrapper">
          <div className="section-eyebrow">QUIZ</div>
          <div className="decision-header-block">
            <h2 className="decision-main-title">No quizzes for this case yet</h2>
            <div className="section-divider" />
            <p className="decision-main-subtitle writing-task-subtitle">
              Continue to the writing task to explain your decision.
            </p>
          </div>
          <div className="feedback-actions case-quiz-actions">
            {typeof onContinueToWriting === 'function' ? (
              <button type="button" className="decision-submit-btn" onClick={onContinueToWriting}>
                Continue to Oxford Tutorial
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  if (phase === 'done') {
    return (
      <section className="decision-section-animated natively-scrollable case-quiz-section">
        <div className="decision-wrapper case-quiz-wrapper">
          <div className="section-eyebrow">QUIZ</div>
          <div className="decision-header-block">
            <h2 className="decision-main-title">Quizzes complete</h2>
            <div className="section-divider" />
            <p className="decision-main-subtitle writing-task-subtitle">
              Score: {score.correct}/{score.answered}. Your answers were saved and written to your
              reasoning and knowledge graph.
            </p>
          </div>
          <div className="feedback-actions case-quiz-actions">
            {typeof onContinueToWriting === 'function' ? (
              <button type="button" className="decision-submit-btn" onClick={onContinueToWriting}>
                Continue to Oxford Tutorial
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="decision-section-animated natively-scrollable case-quiz-section" data-tour="case-quiz">
      <div className="decision-wrapper case-quiz-wrapper">
        <div className="section-eyebrow">QUIZ</div>
        <div className="decision-header-block">
          <h2 className="decision-main-title">Answer reasoning &amp; knowledge questions</h2>
          <div className="section-divider" />
          <p className="case-assessment-kicker case-assessment-kicker--bold case-quiz-phase-label">
            {phase === 'reasoning' ? 'Reasoning test' : 'Knowledge test'} · Question {index + 1} of{' '}
            {questions.length}
          </p>
          <p className="case-tutorial-question-text">{current?.stem}</p>
        </div>

        <div className="strategic-options-animated case-quiz-options">
          <div className="options-list natively-scrollable" role="radiogroup" aria-label="Answer choices">
            {(current?.options || []).map((opt, optIndex) => {
              const letter =
                String(opt.option_key || '').toUpperCase() || String.fromCharCode(65 + optIndex);
              const selected = selectedOptionId === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`option-row ${selected ? 'selected' : ''}`}
                  disabled={Boolean(lastResult)}
                  onClick={() => setSelectedOptionId(opt.id)}
                >
                  <div className="option-row-content">
                    <span className="option-number">{letter}</span>
                    <p className="option-text">{opt.option_text}</p>
                  </div>
                  <div className="option-indicator">
                    {selected ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {lastResult ? (
          <div className="feedback-content case-quiz-result">
            <h4>{lastResult.is_correct ? 'Correct' : 'Not quite'}</h4>
            <p>
              {lastResult.is_correct
                ? lastResult.why_correct || 'Well reasoned.'
                : lastResult.why_incorrect || 'Review the case evidence and try the next item.'}
            </p>
            <button type="button" className="decision-submit-btn" onClick={goNext}>
              {index + 1 < questions.length
                ? 'Next question'
                : phase === 'reasoning' && (payload?.knowledge || []).length
                  ? 'Continue to Knowledge Test'
                  : 'Finish quizzes'}
            </button>
          </div>
        ) : (
          <div className="case-quiz-actions">
            <button
              type="button"
              className="decision-submit-btn decision-submit-btn--compact"
              disabled={!selectedOptionId || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Saving…' : 'Submit Answer'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseQuizPanel;
